import { useRef } from 'react';
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { FieldError } from './FieldError';

interface Props<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label?: string;
}

export const SortCodeInput = <T extends FieldValues>({ name, control, label }: Props<T>) => {
  const {
    field,
    fieldState: { error },
  } = useController({ name, control });
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const value: string = ((field.value as string) ?? '').replace(/\D/g, '').slice(0, 6);
  const parts = [value.slice(0, 2), value.slice(2, 4), value.slice(4, 6)];

  const update = (i: number, v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 2);
    const next = [...parts];
    next[i] = digits;
    field.onChange(next.join(''));
    if (digits.length === 2 && i < 2) refs.current[i + 1]?.focus();
  };

  return (
    <div>
      {label && <label className="lbl">{label}</label>}
      <div className="mt-1.5 flex items-center gap-2">
        {parts.map((p, i) => (
          <span key={i} className="flex items-center gap-2">
            <input
              ref={(el) => {
                refs.current[i] = el;
              }}
              value={p}
              onChange={(e) => update(i, e.target.value)}
              onBlur={field.onBlur}
              maxLength={2}
              inputMode="numeric"
              placeholder="00"
              className={`w-[60px] rounded-sm border-[1.5px] border-ink-200 bg-white px-2 py-2.5 text-center text-sm text-ink-700 outline-none focus:border-teal ${
                error ? 'inp-error' : ''
              }`}
            />
            {i < 2 && <span className="text-lg font-bold text-ink-300">-</span>}
          </span>
        ))}
      </div>
      <FieldError message={error?.message} />
    </div>
  );
};
